import React from 'react';
import { Card, Col, Flex, Progress, Row, Statistic, Skeleton } from 'antd';
import { Wrapper } from './styled';
import { GoTasklist } from 'react-icons/go';
import { BsHandThumbsUp } from 'react-icons/bs';
import { BsHandThumbsDown } from 'react-icons/bs';
import { IoTimeOutline } from 'react-icons/io5';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { COLORS } from '../../theme';
import { useGetDashboardDataQuery } from '../../redux/slices/reviews';
import './styles.css';
import { DashboardData } from 'models/reviews';

// Register necessary components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const { data: dashboardData, isLoading, error } = useGetDashboardDataQuery();

  // Chart options with vertical bars (indexAxis: "y")
  const options: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Accepted vs Rejected Reviews per User' },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: { display: true, text: 'Number of Reviews' },
        grid: {
          display: false,
        },
      },
      y: {
        title: { display: true, text: 'Users' },
        grid: {
          display: false, // 💥 Remove vertical grid lines
        },
      },
    },
  };

  // Chart options for views
  const optionsViews: ChartOptions<'bar'> = {
    indexAxis: 'y', // Horizontal bar chart
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Accepted vs Rejected Reviews per View',
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Reviews',
        },
        grid: {
          display: false,
        },
      },
      y: {
        title: {
          display: true,
          text: 'View',
        },
        grid: {
          display: false,
        },
      },
    },
  };

  // If loading, show skeleton instead of spinner
  if (isLoading) {
    return (
      <Wrapper>
        <Row gutter={16}>
          {[...Array(4)].map((_, index) => (
            <Col span={6} key={index}>
              <Card>
                <Skeleton active paragraph={{ rows: 1 }} />
              </Card>
            </Col>
          ))}
        </Row>
        <Row gutter={16} style={{ marginTop: 24 }}>
          <Col span={12}>
            <Card>
              <Skeleton.Button active block style={{ height: 150 }} />
            </Card>
          </Col>
          <Col span={12}>
            <Card>
              <Skeleton.Button active block style={{ height: 150 }} />
            </Card>
          </Col>
        </Row>
      </Wrapper>
    );
  }

  // If there's an error, show an error message
  if (error) {
    return (
      <Wrapper>
        <Flex justify="center" align="center" style={{ height: '100%' }}>
          <p>Error loading dashboard data. Please try again later.</p>
        </Flex>
      </Wrapper>
    );
  }

  // If no data, show a message
  if (!dashboardData) {
    return (
      <Wrapper>
        <Flex justify="center" align="center" style={{ height: '100%' }}>
          <p>No dashboard data available.</p>
        </Flex>
      </Wrapper>
    );
  }

  // Prepare data for the user reviews chart
  type ReviewByUser = NonNullable<DashboardData['reviewsByUser']>[number];
  const userLabels = (dashboardData.reviewsByUser || []).map((user: ReviewByUser) => user.name);
  const userAccepted = (dashboardData.reviewsByUser || []).map(
    (user: ReviewByUser) => user.accepted
  );
  const userRejected = (dashboardData.reviewsByUser || []).map(
    (user: ReviewByUser) => user.rejected
  );

  const userData: ChartData<'bar'> = {
    labels: userLabels,
    datasets: [
      {
        label: 'Accepted Reviews',
        data: userAccepted,
        backgroundColor: '#2E7D32',
      },
      {
        label: 'Rejected Reviews',
        data: userRejected,
        backgroundColor: '#C62828',
      },
    ],
  };

  // Prepare data for the views chart if available
  type ReviewByView = NonNullable<DashboardData['reviewsByView']>[number];

  let viewsData: ChartData<'bar'> | null = null;

  if (dashboardData.reviewsByView) {
    const viewLabels = (dashboardData.reviewsByView || []).map((view: ReviewByView) => view.name);
    const viewAccepted = (dashboardData.reviewsByView || []).map(
      (view: ReviewByView) => view.accepted
    );
    const viewRejected = (dashboardData.reviewsByView || []).map(
      (view: ReviewByView) => view.rejected
    );
    // Pending is not in the API response, so fill with zeros
    const viewPending = (dashboardData.reviewsByView || []).map(() => 0);

    viewsData = {
      labels: viewLabels,
      datasets: [
        {
          label: 'Accepted Reviews',
          data: viewAccepted,
          backgroundColor: '#2E7D32',
        },
        {
          label: 'Rejected Reviews',
          data: viewRejected,
          backgroundColor: '#C62828',
        },
        {
          label: 'Pending Reviews',
          data: viewPending,
          backgroundColor: '#757575',
        },
      ],
    };
  }

  // Get the raw review counts
  const totalReviews = dashboardData.reviews.totalReviews;
  const acceptedReviews = dashboardData.reviews.acceptedReviews;
  const rejectedReviews = dashboardData.reviews.rejectedReviews;
  const pendingReviews = dashboardData.reviews.pendingReviews;

  // Calculate percentages with 3 decimal places
  // For total, we're showing the percentage of total against itself (always 100%)
  const totalPercent = 100;

  // For accepted, calculate percentage of total with 3 decimal places
  const acceptedPercent =
    totalReviews > 0 ? parseFloat(((acceptedReviews / totalReviews) * 100).toFixed(2)) : 0;

  // For rejected, calculate percentage of total with 3 decimal places
  const rejectedPercent =
    totalReviews > 0 ? parseFloat(((rejectedReviews / totalReviews) * 100).toFixed(2)) : 0;

  // For pending, calculate percentage of total with 3 decimal places
  const pendingPercent =
    totalReviews > 0 ? parseFloat(((pendingReviews / totalReviews) * 100).toFixed(2)) : 0;

  return (
    <Wrapper>
      <Row gutter={16}>
        <Col span={6}>
          <Card className="card-shadow" style={{ backgroundColor: '#FFFFFF' }}>
            <Flex justify={'space-between'}>
              <Statistic
                title="Total Reviews"
                value={dashboardData.reviews.totalReviews}
                valueStyle={{ color: '#2E7D32' }}
                prefix={<GoTasklist />}
              />
              <Progress
                type="circle"
                trailColor="#42A5F5"
                strokeColor="#42A5F5"
                percent={totalPercent}
                size={80}
                className="total-reviews-progress-bar"
              />
            </Flex>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="card-shadow" style={{ backgroundColor: '#E8F5E9' }}>
            <Flex justify={'space-between'}>
              <Statistic
                title="Accepted Reviews"
                value={dashboardData.reviews.acceptedReviews}
                valueStyle={{ color: '#2E7D32' }}
                prefix={<BsHandThumbsUp />}
              />
              <Progress
                className="accepted-reviews-progress-bar"
                strokeColor="#66BB6A"
                type="circle"
                percent={acceptedPercent}
                size={80}
              />
            </Flex>
          </Card>
        </Col>

        <Col span={6}>
          <Card className="card-shadow" style={{ backgroundColor: '#FFEBEE' }}>
            <Flex justify={'space-between'}>
              <Statistic
                title="Rejected Reviews"
                value={dashboardData.reviews.rejectedReviews}
                valueStyle={{ color: '#C62828' }}
                prefix={<BsHandThumbsDown />}
              />
              <Progress
                strokeColor="#EF5350"
                className="rejected-reviews-progress-bar"
                type="circle"
                percent={rejectedPercent}
                size={80}
              />
            </Flex>
          </Card>
        </Col>

        <Col span={6}>
          <Card className="card-shadow" style={{ backgroundColor: '#ECEFF1' }}>
            <Flex justify={'space-between'}>
              <Statistic
                title="Pending Reviews"
                value={dashboardData.reviews.pendingReviews}
                valueStyle={{ color: '#455A64' }}
                prefix={<IoTimeOutline />}
              />
              <Progress
                strokeColor="#90A4AE"
                className="pending-reviews-progress-bar"
                type="circle"
                percent={pendingPercent}
                size={80}
              />
            </Flex>
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col span={12}>
          <Card variant="borderless">
            <Bar data={userData} options={options} color={COLORS.secondary} height={150} />
          </Card>
        </Col>
        <Col span={12}>
          <Card variant="borderless">
            {dashboardData.reviewsByView && viewsData ? (
              <Bar data={viewsData} options={optionsViews} height={150} />
            ) : (
              <Flex justify="center" align="center" style={{ height: '150px' }}>
                <p>View data is not available yet.</p>
              </Flex>
            )}
          </Card>
        </Col>
      </Row>
    </Wrapper>
  );
};

export default Dashboard;
